import { MutableRefObject, useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from '@components/button/Button';
import Icon, { IconSize } from '@components/icon/Icon';
import useOnClickOutside from '@utils/hooks';

interface ConfirmationModalV2Props {
  title?: string;
  description?: string;
  className?: string;
  contentClassName?: string;
  iconName?: string;
  iconSize?: IconSize;
  confirmButtonLabel?: string;
  isConfirmButtonDisabled?: boolean;
  isConfirmButtonLoading?: boolean;
  onConfirm: () => void;
  cancelButtonLabel?: string;
  onCancel: () => void;
  isClickOutsideRequired?: boolean;
  children?: React.ReactNode;
}

const ConfirmationModalV2 = ({
  title,
  description,
  className = '',
  contentClassName = '',
  iconName,
  iconSize = '48',
  onConfirm,
  onCancel,
  confirmButtonLabel = 'Confirm',
  isConfirmButtonDisabled = false,
  isConfirmButtonLoading = false,
  cancelButtonLabel = 'Cancel',
  isClickOutsideRequired = true,
  children
}: ConfirmationModalV2Props) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    if (isClickOutsideRequired && onCancel) {
      e.stopPropagation();
      onCancel();
    }
  });

  const modalUi = (
    <div className="fixed left-0 top-0 z-[60] flex size-full items-center justify-center">
      {/* Modal Overlay */}
      <div className="absolute size-full bg-black opacity-30" />

      {/* Modal Main Content */}
      <div
        className={`relative mx-auto flex max-h-[98%] w-120 flex-col overflow-hidden rounded-lg bg-cofeeNeutral1 ${className}`}
        ref={modalRef}
      >
        <div
          className={`flex flex-col items-center justify-center gap-8 p-6 ${contentClassName}`}
        >
          {iconName && (
            <div className="flex items-center justify-center">
              <Icon name={iconName} size={iconSize} />
            </div>
          )}
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            {title && (
              <span className="text-lg font-semibold text-cofeeNeutral10">
                {title}
              </span>
            )}
            {description && (
              <span className="text-base font-normal text-cofeeNeutral7">
                {description}
              </span>
            )}
          </div>
          {children}
          <div className="flex w-full items-center justify-center gap-2">
            <Button
              variant="tertiary"
              handleClick={onCancel}
              className="w-full"
            >
              {cancelButtonLabel}
            </Button>
            <Button
              variant="primary"
              handleClick={onConfirm}
              className="w-full !text-cofeeNeutral1"
              disabled={isConfirmButtonDisabled}
              loading={isConfirmButtonLoading}
            >
              {confirmButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalUi, document.body);
};

export default ConfirmationModalV2;
